import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Loader2,
  LogOut,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import Logo from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Full Refurbishment",
  "Painting & Decorating",
  "Bathroom Installation",
  "Kitchen Installation",
  "End of Tenancy Cleaning",
];

const FIELD =
  "w-full bg-transparent border border-white/15 rounded-[2px] px-4 py-3 text-[#f5f5f0] placeholder:text-[#a3a3a3]/60 outline-none focus:border-[#c5832b] transition-colors duration-300";
const LABEL =
  "block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2";

const formatErr = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(", ");
  return String(detail);
};

const imgSrc = (u) =>
  !u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}/api/files/${u}`;

const emptyForm = {
  title: "",
  location: "",
  category: "",
  description: "",
  details: "",
  year: new Date().getFullYear(),
  order: 5,
  before_url: "",
  after_url: "",
  gallery: [],
};

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      onLogin(data);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm bg-[#1a1a1a] border border-white/10 rounded-[2px] p-8"
      >
        <img
          src="/logo-full.png"
          alt="Preece Refurbishments"
          className="h-36 mx-auto mb-6 rounded-[4px]"
        />
        <h1 className="font-serif text-3xl text-[#f5f5f0] mb-2">Admin sign in</h1>
        <p className="text-sm text-[#a3a3a3] mb-8">
          Manage your completed projects and photos.
        </p>

        {error && (
          <p
            data-testid="admin-login-error"
            className="mb-6 text-sm text-red-400 border border-red-400/30 bg-red-400/10 px-4 py-3 rounded-[2px]"
          >
            {error}
          </p>
        )}

        <label htmlFor="admin-email" className={LABEL}>Email</label>
        <input
          id="admin-email"
          data-testid="admin-email-input"
          type="email"
          autoComplete="email"
          className={FIELD}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <label htmlFor="admin-password" className={`${LABEL} mt-5`}>Password</label>
        <input
          id="admin-password"
          data-testid="admin-password-input"
          type="password"
          autoComplete="current-password"
          className={FIELD}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={busy}
          className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#c5832b] hover:bg-[#d9953c] disabled:opacity-60 text-[#121212] font-semibold px-8 py-4 rounded-[2px] transition-colors duration-300"
        >
          {busy ? <Loader2 size={18} className="animate-spin" /> : "Sign in"}
        </button>
        <button
          type="button"
          data-testid="admin-back-to-site"
          onClick={() => (window.location.href = "/")}
          className="mt-4 w-full text-sm text-[#a3a3a3] hover:text-[#c5832b] transition-colors duration-300"
        >
          Back to website
        </button>
      </form>
    </div>
  );
}

function FileField({ label, testid, file, existing, onFile }) {
  const preview = file ? URL.createObjectURL(file) : imgSrc(existing);
  return (
    <div>
      <span className={LABEL}>{label}</span>
      <label
        data-testid={testid}
        className="block cursor-pointer border border-dashed border-white/20 hover:border-[#c5832b] rounded-[2px] overflow-hidden transition-colors duration-300"
      >
        {preview ? (
          <img src={preview} alt={label} className="w-full h-36 object-cover" />
        ) : (
          <span className="flex items-center justify-center h-36 text-sm text-[#a3a3a3]">
            Click to upload photo
          </span>
        )}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] || null)}
        />
      </label>
      {file && (
        <p className="mt-1 text-xs text-[#c5832b] flex items-center gap-1">
          <Check size={12} /> New photo selected
        </p>
      )}
    </div>
  );
}

function ManagerView({ admin, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const galleryRef = useRef(null);

  const load = () => {
    axios
      .get(`${API}/projects`)
      .then((r) => setProjects(r.data))
      .catch(() => toast.error("Could not load projects"));
    axios
      .get(`${API}/quotes`, { withCredentials: true })
      .then((r) => setQuotes(r.data))
      .catch(() => toast.error("Could not load enquiries"));
  };

  useEffect(() => {
    load();
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const reset = () => {
    setForm(emptyForm);
    setBeforeFile(null);
    setAfterFile(null);
    setGalleryFiles([]);
    setEditingId(null);
    if (beforeRef.current) beforeRef.current.value = "";
    if (afterRef.current) afterRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${API}/admin/upload`, fd, {
      withCredentials: true,
    });
    return data.path;
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category) {
      toast.error("Project needs at least a title and category.");
      return;
    }
    setBusy(true);
    try {
      const before_url = (beforeFile ? await uploadOne(beforeFile) : "") || form.before_url;
      const after_url = (afterFile ? await uploadOne(afterFile) : "") || form.after_url;
      const uploaded = [];
      for (const f of galleryFiles) {
        uploaded.push(await uploadOne(f));
      }
      const body = {
        ...form,
        year: Number(form.year) || new Date().getFullYear(),
        order: Number(form.order) || 0,
        before_url,
        after_url,
        gallery: [...form.gallery, ...uploaded],
      };
      if (editingId) {
        await axios.put(`${API}/admin/projects/${editingId}`, body, { withCredentials: true });
        toast.success("Project updated");
      } else {
        await axios.post(`${API}/admin/projects`, body, { withCredentials: true });
        toast.success("Project added — it's live on the site now");
      }
      reset();
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
      if (err.response?.status === 401) onLogout();
    } finally {
      setBusy(false);
    }
  };

  const edit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      location: p.location || "",
      category: p.category,
      description: p.description || "",
      details: p.details || "",
      year: p.year,
      order: p.order || 0,
      before_url: p.before_url || "",
      after_url: p.after_url || "",
      gallery: p.gallery || [],
    });
    setBeforeFile(null);
    setAfterFile(null);
    setGalleryFiles([]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/projects/${p.id}`, { withCredentials: true });
      toast.success("Project deleted");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10">
      <header className="flex items-center justify-between mb-12">
        <button onClick={() => (window.location.href = "/")} data-testid="admin-home-link" className="cursor-pointer">
          <Logo />
        </button>
        <div className="flex items-center gap-4">
          <span className="hidden sm:block text-sm text-[#a3a3a3]">{admin.email}</span>
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="flex items-center gap-2 border border-white/15 hover:border-[#c5832b] hover:text-[#c5832b] text-sm px-4 py-2 rounded-[2px] transition-colors duration-300"
          >
            <LogOut size={15} /> Log out
          </button>
        </div>
      </header>

      <h1 className="font-serif text-4xl md:text-5xl text-[#f5f5f0] mb-2">
        {editingId ? "Edit project" : "Add a project"}
      </h1>
      <p className="text-[#a3a3a3] mb-10">
        Upload your own before &amp; after photos — new projects appear on the
        website immediately.
      </p>

      <form
        data-testid="admin-project-form"
        onSubmit={save}
        className="bg-[#1a1a1a] border border-white/10 rounded-[2px] p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <div>
          <label htmlFor="p-title" className={LABEL}>Title *</label>
          <input id="p-title" data-testid="admin-title-input" className={FIELD} placeholder="e.g. Maple Grove Kitchen" value={form.title} onChange={set("title")} />
        </div>
        <div>
          <label htmlFor="p-location" className={LABEL}>Location</label>
          <input id="p-location" data-testid="admin-location-input" className={FIELD} placeholder="e.g. Cheltenham" value={form.location} onChange={set("location")} />
        </div>
        <div>
          <label htmlFor="p-category" className={LABEL}>Service *</label>
          <select id="p-category" data-testid="admin-category-select" className={`${FIELD} appearance-none ${form.category ? "text-[#f5f5f0]" : "text-[#a3a3a3]/60"}`} value={form.category} onChange={set("category")}>
            <option value="" disabled>Choose a service…</option>
            {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="p-year" className={LABEL}>Year</label>
            <input id="p-year" data-testid="admin-year-input" type="number" className={FIELD} value={form.year} onChange={set("year")} />
          </div>
          <div>
            <label htmlFor="p-order" className={LABEL}>Display order</label>
            <input id="p-order" data-testid="admin-order-input" type="number" className={FIELD} value={form.order} onChange={set("order")} />
          </div>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="p-desc" className={LABEL}>Description</label>
          <textarea id="p-desc" data-testid="admin-description-textarea" rows={3} className={`${FIELD} resize-none`} placeholder="A short summary shown on the project card" value={form.description} onChange={set("description")} />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="p-details" className={LABEL}>Job details (shown on the project's own page)</label>
          <textarea id="p-details" data-testid="admin-details-textarea" rows={5} className={`${FIELD} resize-none`} placeholder={"Scope of works, room by room…\nMaterials and finishes…\nTimeline and any special challenges…"} value={form.details} onChange={set("details")} />
        </div>
        <FileField label="Before photo" testid="admin-before-upload" file={beforeFile} existing={form.before_url} onFile={setBeforeFile} />
        <FileField label="After photo" testid="admin-after-upload" file={afterFile} existing={form.after_url} onFile={setAfterFile} />
        <div className="md:col-span-2">
          <span className={LABEL}>Extra gallery photos (optional — shown on the project's own page)</span>
          <label
            data-testid="admin-gallery-upload"
            className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-white/20 hover:border-[#c5832b] rounded-[2px] py-6 text-sm text-[#a3a3a3] hover:text-[#c5832b] transition-colors duration-300"
          >
            <Plus size={16} />
            {galleryFiles.length > 0
              ? `${galleryFiles.length} new photo${galleryFiles.length > 1 ? "s" : ""} selected — click to add more`
              : "Click to add extra photos of this job"}
            <input
              ref={galleryRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
            />
          </label>
          {form.gallery.length > 0 && (
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">
              {form.gallery.map((g, gi) => (
                <div key={g + gi} className="relative">
                  <img
                    src={imgSrc(g)}
                    alt={`Gallery photo ${gi + 1}`}
                    className="w-full aspect-square object-cover rounded-[2px]"
                  />
                  <button
                    type="button"
                    data-testid={`admin-gallery-remove-${gi}`}
                    onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((x) => x !== g) }))}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/70 text-white/80 hover:text-red-400 rounded-[2px] flex items-center justify-center"
                    aria-label="Remove photo"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-2 flex items-center justify-end gap-4">
          {editingId && (
            <button type="button" data-testid="admin-cancel-edit-button" onClick={reset} className="text-sm text-[#a3a3a3] hover:text-[#f5f5f0] transition-colors duration-300">
              Cancel edit
            </button>
          )}
          <button
            type="submit"
            data-testid="admin-save-project-button"
            disabled={busy}
            className="inline-flex items-center gap-2 bg-[#c5832b] hover:bg-[#d9953c] disabled:opacity-60 text-[#121212] font-semibold px-8 py-4 rounded-[2px] transition-colors duration-300"
          >
            {busy ? (<><Loader2 size={17} className="animate-spin" /> Saving…</>) : (<>{editingId ? <Pencil size={16} /> : <Plus size={16} />} {editingId ? "Update project" : "Add project"}</>)}
          </button>
        </div>
      </form>

      <h2 className="font-serif text-3xl text-[#f5f5f0] mt-16 mb-6">
        Live projects <span className="text-[#a3a3a3] text-lg">({projects.length})</span>
      </h2>
      <div data-testid="admin-project-list" className="space-y-4">
        {projects.map((p, i) => (
          <div
            key={p.id}
            data-testid={`admin-project-row-${i}`}
            className="flex flex-col sm:flex-row sm:items-center gap-4 bg-[#1a1a1a] border border-white/10 rounded-[2px] p-4"
          >
            <div className="flex gap-2 shrink-0">
              <img src={imgSrc(p.before_url)} alt={`${p.title} before`} className="w-20 h-14 object-cover rounded-[2px]" />
              <img src={imgSrc(p.after_url)} alt={`${p.title} after`} className="w-20 h-14 object-cover rounded-[2px] border border-[#c5832b]/40" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-[#c5832b]">{p.category} · {p.year}</p>
              <p className="font-serif text-xl text-[#f5f5f0] truncate">{p.title}</p>
              {p.location && <p className="text-sm text-[#a3a3a3]">{p.location}</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`admin-edit-button-${i}`} onClick={() => edit(p)} className="flex items-center gap-2 text-sm border border-white/15 hover:border-[#c5832b] hover:text-[#c5832b] px-4 py-2 rounded-[2px] transition-colors duration-300">
                <Pencil size={14} /> Edit
              </button>
              <button data-testid={`admin-delete-button-${i}`} onClick={() => remove(p)} className="flex items-center gap-2 text-sm border border-red-400/30 text-red-400 hover:bg-red-400/10 px-4 py-2 rounded-[2px] transition-colors duration-300">
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        ))}
        {projects.length === 0 && (
          <p className="text-[#a3a3a3] border border-dashed border-white/15 rounded-[2px] p-8 text-center">
            No projects yet — add your first one above.
          </p>
        )}
      </div>

      <h2 className="font-serif text-3xl text-[#f5f5f0] mt-16 mb-2">
        Quote enquiries <span className="text-[#a3a3a3] text-lg">({quotes.length})</span>
      </h2>
      <p className="text-sm text-[#a3a3a3] mb-6">
        Submitted through the website form — newest first.
      </p>
      <div data-testid="admin-quotes-list" className="space-y-4 pb-10">
        {quotes.map((q, i) => (
          <div
            key={q.id}
            data-testid={`admin-quote-row-${i}`}
            className="bg-[#1a1a1a] border border-white/10 rounded-[2px] p-5 md:p-6"
          >
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
              <div>
                <p className="font-serif text-xl text-[#f5f5f0]">{q.name}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#c5832b] mt-1">
                  {q.service}
                </p>
              </div>
              <p className="text-xs text-[#a3a3a3] shrink-0">
                {new Date(q.created_at).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            </div>
            <div className="mt-3 text-sm text-[#f5f5f0]/80 space-y-2">
              <p data-testid={`admin-quote-contact-${i}`}>{q.email}{q.phone ? ` · ${q.phone}` : ""}</p>
              {q.message && (
                <p className="text-[#a3a3a3] leading-relaxed">{q.message}</p>
              )}
            </div>
            <div className="mt-4 flex gap-4">
              <a
                href={`mailto:${q.email}`}
                data-testid={`admin-quote-reply-${i}`}
                className="text-xs font-semibold text-[#c5832b] hover:text-[#d9953c] transition-colors duration-300"
              >
                Reply by email
              </a>
              {q.phone && (
                <a
                  href={`tel:${q.phone.replace(/\s/g, "")}`}
                  data-testid={`admin-quote-call-${i}`}
                  className="text-xs font-semibold text-[#a3a3a3] hover:text-[#f5f5f0] transition-colors duration-300"
                >
                  Call
                </a>
              )}
            </div>
          </div>
        ))}
        {quotes.length === 0 && (
          <p className="text-[#a3a3a3] border border-dashed border-white/15 rounded-[2px] p-8 text-center">
            No enquiries yet — they'll appear here when someone requests a quote.
          </p>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setAdmin(r.data))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    setAdmin(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#c5832b]" />
      </div>
    );
  }

  return admin ? (
    <ManagerView admin={admin} onLogout={logout} />
  ) : (
    <LoginView onLogin={setAdmin} />
  );
}
